/**
 * Admin Audit Log Page
 *
 * View all admin actions and changes
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Logger, LogCategory } from '@/utils/logger';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useAdminStore, useAuditLogs, useAdminLoading } from '@/stores/adminStore';
import type { AuditAction } from '@/types';
import {
  FileText,
  Loader2,
  Filter,
  User,
  CreditCard,
  Tag,
} from 'lucide-react';

export function AdminAuditLogPage() {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customer');

  const { fetchAuditLogs } = useAdminStore();
  const auditLogs = useAuditLogs();
  const isLoading = useAdminLoading();

  const [filterAction, setFilterAction] = useState<AuditAction | ''>('');

  useEffect(() => {
    Logger.info(LogCategory.UI, 'AdminAuditLogPage mounted', 'AdminAuditLogPage');
    fetchAuditLogs({ companyId: customerId || undefined, limit: 100 });
  }, [fetchAuditLogs, customerId]);

  const filteredLogs = filterAction
    ? auditLogs.filter((log) => log.action === filterAction)
    : auditLogs;

  const getActionIcon = (action: AuditAction) => {
    if (action.startsWith('customer')) return <User className="h-4 w-4" />;
    if (action.startsWith('billing')) return <CreditCard className="h-4 w-4" />;
    if (action.startsWith('promotion')) return <Tag className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const getActionColor = (action: AuditAction) => {
    if (action.includes('delete')) return 'danger';
    if (action.includes('create')) return 'success';
    if (action.includes('edit') || action.includes('update')) return 'warning';
    return 'info';
  };

  const getActionLabel = (action: AuditAction) => {
    return action.split('.').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Audit Log</h2>
          <p className="text-slate-500 mt-1">
            {customerId
              ? 'Activity log for selected customer'
              : 'Track all admin actions and changes'}
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Filter by action:</span>
              </div>
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value as AuditAction | '')}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Actions</option>
                <optgroup label="Customer Actions">
                  <option value="customer.view">View Customer</option>
                  <option value="customer.edit">Edit Customer</option>
                  <option value="customer.impersonate">Impersonate</option>
                  <option value="customer.deactivate">Deactivate</option>
                  <option value="customer.activate">Activate</option>
                </optgroup>
                <optgroup label="Billing Actions">
                  <option value="billing.plan_change">Plan Change</option>
                  <option value="billing.credit_add">Add Credits</option>
                  <option value="billing.credit_remove">Remove Credits</option>
                </optgroup>
                <optgroup label="Promotion Actions">
                  <option value="promotion.create">Create Promotion</option>
                  <option value="promotion.edit">Edit Promotion</option>
                  <option value="promotion.delete">Delete Promotion</option>
                  <option value="promotion.apply">Apply Promotion</option>
                </optgroup>
                <optgroup label="Note Actions">
                  <option value="note.create">Create Note</option>
                  <option value="note.edit">Edit Note</option>
                  <option value="note.delete">Delete Note</option>
                </optgroup>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Audit Log List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : filteredLogs.length > 0 ? (
          <div className="space-y-2">
            {filteredLogs.map((log) => (
              <Card key={log.id} className="hover:border-purple-200 transition-colors">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      {getActionIcon(log.action)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={getActionColor(log.action)} size="sm">
                          {getActionLabel(log.action)}
                        </Badge>
                        {log.targetName && (
                          <span className="text-sm font-medium text-slate-900">
                            {log.targetName}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-slate-600 mb-2">
                        <strong>{log.adminUserName}</strong> ({log.adminUserEmail})
                      </p>

                      {/* Details */}
                      {Object.keys(log.details).length > 0 && (
                        <details className="text-sm">
                          <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                            View details
                          </summary>
                          <pre className="mt-2 p-2 bg-slate-50 rounded text-xs overflow-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </details>
                      )}

                      {/* Timestamp */}
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(log.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No audit logs found</h3>
                <p className="text-slate-500">
                  {filterAction
                    ? 'No logs match the selected filter'
                    : 'Admin actions will appear here'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
